// ============================================
// APP.JS - SIN MÓDULO DE ACTIVOS
// ============================================

import * as Sentry from '@sentry/react';
import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { toast } from 'react-hot-toast';

// Hooks
import { useAuth } from './hooks';
import { usePlants } from './hooks';
import { useEquipments } from './hooks';
import { usePushNotifications } from './hooks/usePushNotifications';

// Components
import { LoadingSpinner } from './components/common';
import LoginView from './components/auth/LoginView';
import ChangePasswordView from './components/auth/ChangePasswordView';
import ForgotPasswordView from './components/auth/ForgotPasswordView';
import DashboardView from './components/dashboard/DashboardView';
import PlantDetailView from './components/plant/PlantDetailView';
import AdminView from './components/admin/AdminView';
import FaultHistoryView from './components/fault/FaultHistoryView';
import CalibrationHistoryView from './components/calibration/CalibrationHistoryView';
import CompanySelectView from './components/company/CompanySelectView';

// Modals
import {
  UserModal,
  PlantModal,
  EquipmentModal,
  EquipmentDetailModal,
  CalibrationModal,
  MaintenanceModal,
  FaultModal,
  FaultUpdateModal,
  PrivacyConsentModal,
  MyDataModal
} from './components/modals';

// Services
import {
  userService,
  companyService,
  calibrationService,
  maintenanceService,
  faultService
} from './services/api';

function App() {
  const [view, setView] = useState('login');
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // selectedCompany: '__unset__' = no elegida aún | null = global | 1 | 2
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const saved = localStorage.getItem('selectedCompany');
    return saved !== null ? JSON.parse(saved) : '__unset__';
  });

  // Estado de modales
  const [modals, setModals] = useState({
    user: { show: false, data: null },
    plant: { show: false, data: null },
    equipment: { show: false, data: null, plantId: null, line: null },
    equipmentDetail: { show: false, data: null },
    calibration: { show: false, data: null, equipment: null },
    maintenance: { show: false, data: null, equipment: null },
    fault: { show: false, data: null, plant: null, equipment: null },
    faultUpdate: { show: false, data: null },
    myData: { show: false },
  });

  // Modal de privacidad: mostrar si el usuario está autenticado y no ha aceptado
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const auth = useAuth();
  const plants = usePlants(auth.isAuthenticated);
  const equipments = useEquipments(auth.isAuthenticated);
  const { subscribe: subscribePush } = usePushNotifications();

  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [calibrations, setCalibrations] = useState([]);
  const [maintenances, setMaintenances] = useState([]);
  const [faults, setFaults] = useState([]);

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (!auth.isAuthenticated && view !== 'forgot-password') {
      setView('login');
      setSelectedPlant(null);
    } else if (auth.isAuthenticated && auth.user?.force_password_change === 1 && view !== 'change-password') {
      setView('change-password');
    } else if (auth.isAuthenticated && auth.user && !auth.user.privacy_accepted_at) {
      setShowPrivacyModal(true);
  } else if (auth.isAuthenticated && !auth.user?.force_password_change) {
      // Redirigir post-login según scope/rol
      const isGlobalAdmin = auth.user?.scope === 'global';
      const isGerenteGeneral = auth.user?.role === 'Gerente General';
      const gerenteGeneralHasMultiple = isGerenteGeneral && (auth.user?.companies || []).length > 1;
      if ((isGlobalAdmin || gerenteGeneralHasMultiple) && selectedCompany === '__unset__' && view !== 'company-select') {
        setView('company-select');
      } else if (auth.user?.scope === 'company' && !gerenteGeneralHasMultiple) {
        // Siempre anclar al company_id del token, ignorando localStorage
        const cid = auth.user.company_id;
        if (selectedCompany !== cid) {
          setSelectedCompany(cid);
          localStorage.setItem('selectedCompany', JSON.stringify(cid));
        }
        if (view === 'login') setView('dashboard');
      } else if (view === 'login' && selectedCompany !== '__unset__') {
        setView('dashboard');
      }
    }
  }, [auth.isAuthenticated, auth.user, view, selectedCompany]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      loadUsers();
      loadCompanies();
      loadCalibrations();
      loadMaintenances();
      loadFaults();

      // Pedir permiso de notificaciones si aún no se ha decidido
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            subscribePush();
          }
        });
      }
    }
  }, [auth.isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (view === 'admin' || view === 'dashboard') {
      loadUsers();
      loadCalibrations();
      loadMaintenances();
      loadFaults();
    }
  }, [view]);

  const loadUsers = async () => {
    try {
      const data = await userService.getAll();
      setUsers(data);
    } catch (error) {
      console.error('❌ Error cargando usuarios:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await companyService.getAll();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      setCompanies([]); // 403 para no-admin o error de red → sin empresas es correcto
    }
  };

  const loadCalibrations = async () => {
    try {
      const data = await calibrationService.getAll();
      setCalibrations(Array.isArray(data) ? data : []);
    } catch (error) {
      setCalibrations([]);
    }
  };

  const loadMaintenances = async () => {
    try {
      const data = await maintenanceService.getAll();
      setMaintenances(Array.isArray(data) ? data : []);
    } catch (error) {
      setMaintenances([]);
    }
  };

  const loadFaults = async () => {
    try {
      const data = await faultService.getAll();
      setFaults(Array.isArray(data) ? data : []);
    } catch (error) {
      setFaults([]);
    }
  };

  const getCalibrationStatus = (equipmentId) => {
    const equipmentCalibrations = calibrations.filter(c => c.equipment_id === equipmentId);
    if (equipmentCalibrations.length === 0) return 'no-data';

    const latest = equipmentCalibrations.sort((a, b) =>
      new Date(b.proxima_calibracion) - new Date(a.proxima_calibracion)
    )[0];

    const today = new Date();
    const nextDate = new Date(latest.proxima_calibracion);

    if (nextDate < today) return 'expired';

    const daysUntilExpiry = Math.floor((nextDate - today) / (1000 * 60 * 60 * 24));
    if (daysUntilExpiry <= 30) return 'warning';

    return 'valid';
  };

  // ============================================
  // HANDLERS DE EMPRESA
  // ============================================

  const handleSelectCompany = (companyId) => {
    setSelectedCompany(companyId);
    localStorage.setItem('selectedCompany', JSON.stringify(companyId));
    setView('dashboard');
  };

  const handleChangeCompany = () => {
    setSelectedCompany('__unset__');
    localStorage.removeItem('selectedCompany');
    setView('company-select');
  };

  const handleLogout = () => {
    setSelectedCompany('__unset__');
    localStorage.removeItem('selectedCompany');
    auth.logout();
  };

  // ============================================

  const handleSelectPlant = (plant) => {
    setSelectedPlant(plant);
    setView('plant-detail');
  };

  const handleBackToDashboard = () => {
    setSelectedPlant(null);
    setView('dashboard');
  };

  // ============================================
  // HANDLERS DE USUARIOS
  // ============================================

  const handleCreateUser = () => {
    setModals(prev => ({
      ...prev,
      user: { show: true, data: null }
    }));
  };

  const handleEditUser = (user) => {
    setModals(prev => ({
      ...prev,
      user: { show: true, data: user }
    }));
  };

  const handleSubmitUser = async (userData, userId) => {
    try {
      if (userId) {
        const updated = await userService.update(userId, userData);
        setUsers(prev => prev.map(u => u.id === userId ? updated : u));
        toast.success('Usuario actualizado');
      } else {
        const created = await userService.create(userData);
        setUsers(prev => [...prev, created]);
        toast.success('Usuario creado');
      }
      closeModal('user');
    } catch (error) {
      toast.error(error.message || 'Error al guardar usuario');
      throw error;
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    try {
      await userService.delete(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('Usuario eliminado');
    } catch (error) {
      toast.error(error.message || 'Error al eliminar usuario');
    }
  };

  // ============================================
  // HANDLERS DE PLANTAS
  // ============================================

  const handleCreatePlant = () => {
    setModals(prev => ({
      ...prev,
      plant: { show: true, data: null }
    }));
  };

  const handleEditPlant = (plant) => {
    setModals(prev => ({
      ...prev,
      plant: { show: true, data: plant }
    }));
  };

  const handleSubmitPlant = async (plantData, plantId) => {
    try {
      if (plantId) {
        await plants.updatePlant(plantId, plantData);
      } else {
        await plants.createPlant(plantData);
      }
      await plants.loadPlants();
      closeModal('plant');
    } catch (error) {
      throw error;
    }
  };

  // ============================================
  // HANDLERS DE EQUIPOS
  // ============================================

  const handleCreateEquipment = (plantId, line) => {
    const finalPlantId = plantId || selectedPlant?.id;
    const finalLine = line || 'L1';

    if (!finalPlantId) {
      toast.error('Debes seleccionar una planta primero');
      return;
    }

    setModals(prev => ({
      ...prev,
      equipment: {
        show: true,
        data: null,
        plantId: finalPlantId,
        line: finalLine
      }
    }));
  };

  const handleEditEquipment = (equipment) => {
    setModals(prev => ({
      ...prev,
      equipment: {
        show: true,
        data: equipment,
        plantId: equipment.plantId,
        line: equipment.line
      }
    }));
  };

  const handleViewEquipment = (equipment) => {
    setModals(prev => ({
      ...prev,
      equipmentDetail: { show: true, data: equipment }
    }));
  };

  const handleSubmitEquipment = async (equipmentData, equipmentId) => {
    try {
      if (equipmentId) {
        await equipments.updateEquipment(equipmentId, equipmentData);
      } else {
        await equipments.createEquipment(equipmentData);
      }
      await equipments.loadEquipments();
      closeModal('equipment');
    } catch (error) {
      throw error;
    }
  };

  // ============================================
  // HANDLERS DE CALIBRACIONES
  // ============================================

  const handleCreateCalibration = (equipment) => {
    setModals(prev => ({
      ...prev,
      calibration: { show: true, data: null, equipment }
    }));
  };

  const handleEditCalibration = (calibration) => {
    setModals(prev => ({
      ...prev,
      calibration: { show: true, data: calibration, equipment: null }
    }));
  };

  const handleSubmitCalibration = async (calibrationData, calibrationId) => {
    try {
      if (calibrationId) {
        await calibrationService.update(calibrationId, calibrationData);
        toast.success('Calibración actualizada');
      } else {
        await calibrationService.create(calibrationData);
        toast.success('Calibración registrada');
      }
      await loadCalibrations();
      closeModal('calibration');
    } catch (error) {
      toast.error(error.message || 'Error al guardar calibración');
      throw error;
    }
  };

  // ============================================
  // HANDLERS DE MANTENIMIENTOS
  // ============================================

  const handleCreateMaintenance = (equipment) => {
    setModals(prev => ({
      ...prev,
      maintenance: { show: true, data: null, equipment }
    }));
  };

  const handleSubmitMaintenance = async (maintenanceData, maintenanceId) => {
    try {
      if (maintenanceId) {
        await maintenanceService.update(maintenanceId, maintenanceData);
        toast.success('Mantenimiento actualizado');
      } else {
        await maintenanceService.create(maintenanceData);
        toast.success('Mantenimiento registrado');
      }
      await loadMaintenances();
      closeModal('maintenance');
    } catch (error) {
      toast.error(error.message || 'Error al guardar mantenimiento');
      throw error;
    }
  };

  // ============================================
  // HANDLERS DE FALLAS
  // ============================================

  const handleCreateFault = (plant, equipment = null) => {
    setModals(prev => ({
      ...prev,
      fault: { show: true, data: null, plant, equipment }
    }));
  };

  const handleUpdateFault = (fault) => {
    setModals(prev => ({
      ...prev,
      faultUpdate: { show: true, data: fault }
    }));
  };

  const handleSubmitFault = async (faultData, faultId) => {
    try {
      if (faultId) {
        await faultService.update(faultId, faultData);
        toast.success('Falla actualizada correctamente');
      } else {
        await faultService.create(faultData);
        toast.success('Falla registrada - Alertas enviadas por correo');
      }

      await loadFaults();
      await equipments.loadEquipments();

      closeModal('fault');
      closeModal('faultUpdate');

    } catch (error) {
      console.error('❌ Error al guardar falla:', error);
      toast.error(error.message || 'Error al guardar falla');
      throw error;
    }
  };

  // ============================================
  // CERRAR MODALES
  // ============================================

  const closeModal = (modalName) => {
    setModals(prev => ({
      ...prev,
      [modalName]: {
        show: false,
        data: null,
        ...(modalName === 'equipment' ? { plantId: null, line: null } : {}),
        ...(modalName === 'calibration' ? { equipment: null } : {}),
        ...(modalName === 'maintenance' ? { equipment: null } : {}),
        ...(modalName === 'fault' ? { plant: null } : {})
      }
    }));
  };

  const isLoading = auth.loading || plants.loading || equipments.loading;

  const renderView = () => {
    if (!auth.isAuthenticated) {
      if (view === 'forgot-password') {
        return (
          <ForgotPasswordView
            onSubmit={auth.forgotPassword}
            onBack={() => setView('login')}
          />
        );
      }
      return (
        <LoginView
          onLogin={auth.login}
          onForgotPassword={() => setView('forgot-password')}
        />
      );
    }

    if (view === 'change-password') {
      return (
        <ChangePasswordView
          onSubmit={auth.changePassword}
          onSuccess={() => setView('dashboard')}
        />
      );
    }

    if (view === 'company-select') {
      return (
        <CompanySelectView
          user={auth.user}
          companies={companies}
          plants={plants.plants}
          equipments={equipments.equipments}
          plantsLoading={plants.loading}
          onSelectCompany={handleSelectCompany}
          onLogout={handleLogout}
        />
      );
    }

    switch (view) {
      case 'admin':
        return (
          <AdminView
            user={auth.user}
            users={users}
            plants={plants.plants}
            equipments={equipments.equipments}
            companies={auth.user?.scope === 'global' ? companies : []}
            selectedCompany={selectedCompany}
            onBack={handleBackToDashboard}
            onLogout={handleLogout}
            onOpenMyData={() => setModals(prev => ({ ...prev, myData: { show: true } }))}
            onCreateUser={handleCreateUser}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            onCreatePlant={handleCreatePlant}
            onEditPlant={handleEditPlant}
            onDeletePlant={plants.deletePlant}
            onCreateEquipment={handleCreateEquipment}
            onEditEquipment={handleEditEquipment}
            onDeleteEquipment={equipments.deleteEquipment}
            onCompaniesChange={loadCompanies}
          />
        );

      case 'fault-history':
        return (
          <FaultHistoryView
            faults={selectedCompany != null ? faults.filter(f => f.company_id === selectedCompany) : faults}
            plants={plants.plants}
            equipments={equipments.equipments}
            user={auth.user}
            onBack={handleBackToDashboard}
            onLogout={handleLogout}
            onOpenMyData={() => setModals(prev => ({ ...prev, myData: { show: true } }))}
            onViewFault={handleUpdateFault}
          />
        );

      case 'calibration-history':
        return (
          <CalibrationHistoryView
            calibrations={selectedCompany != null ? calibrations.filter(c => c.company_id === selectedCompany) : calibrations}
            plants={plants.plants}
            equipments={equipments.equipments}
            user={auth.user}
            onBack={handleBackToDashboard}
            onLogout={handleLogout}
            onOpenMyData={() => setModals(prev => ({ ...prev, myData: { show: true } }))}
            onViewCalibration={handleEditCalibration}
            getCalibrationStatus={getCalibrationStatus}
          />
        );

      case 'plant-detail':
        return (
          <PlantDetailView
            plant={selectedPlant}
            equipments={equipments.equipments}
            faults={faults.filter(f => f.plant_id === selectedPlant?.id && f.estado !== 'resuelta')}
            user={auth.user}
            onBack={handleBackToDashboard}
            onLogout={handleLogout}
            onOpenMyData={() => setModals(prev => ({ ...prev, myData: { show: true } }))}
            onCreateEquipment={handleCreateEquipment}
            onEditEquipment={handleEditEquipment}
            onDeleteEquipment={equipments.deleteEquipment}
            onViewEquipment={handleViewEquipment}
            onRegisterFault={(equip) => handleCreateFault(selectedPlant, equip || null)}
            onUpdateFault={handleUpdateFault}
            getCalibrationStatus={getCalibrationStatus}
          />
        );

      case 'dashboard':
      default:
        return (
          <DashboardView
            user={auth.user}
            plants={plants.plants}
            equipments={equipments.equipments}
            faults={faults}
            companies={companies}
            selectedCompany={
              auth.user?.scope === 'company' && auth.user?.role !== 'Gerente General'
                ? auth.user.company_id
                : selectedCompany
            }
            onSelectPlant={handleSelectPlant}
            onGoToAdmin={() => setView('admin')}
            onGoToFaultHistory={() => setView('fault-history')}
            onGoToCalibrationHistory={() => setView('calibration-history')}
            onChangeCompany={handleChangeCompany}
            onLogout={handleLogout}
            onOpenMyData={() => setModals(prev => ({ ...prev, myData: { show: true } }))}
            getCalibrationStatus={getCalibrationStatus}
          />
        );
    }
  };

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          success: {
            duration: 3000,
            style: { background: '#10b981', color: '#fff' },
          },
          error: {
            duration: 4000,
            style: { background: '#ef4444', color: '#fff' },
          },
        }}
      />

      {isLoading && <LoadingSpinner />}

      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        {renderView()}
      </div>

      <UserModal
        show={modals.user.show}
        user={modals.user.data}
        plants={plants.plants}
        companies={companies}
        currentUser={auth.user}
        onClose={() => closeModal('user')}
        onSubmit={handleSubmitUser}
      />

      <PlantModal
        show={modals.plant.show}
        plant={modals.plant.data}
        onClose={() => closeModal('plant')}
        onSubmit={handleSubmitPlant}
      />

      <EquipmentModal
        show={modals.equipment.show}
        equipment={modals.equipment.data}
        plantId={modals.equipment.plantId}
        line={modals.equipment.line}
        onClose={() => closeModal('equipment')}
        onSubmit={handleSubmitEquipment}
      />

      <EquipmentDetailModal
        show={modals.equipmentDetail.show}
        equipment={modals.equipmentDetail.data}
        plant={selectedPlant}
        maintenances={Array.isArray(maintenances) ? maintenances.filter(m => m.equipment_id === modals.equipmentDetail.data?.id) : []}
        calibrations={Array.isArray(calibrations) ? calibrations.filter(c => c.equipment_id === modals.equipmentDetail.data?.id) : []}
        onClose={() => closeModal('equipmentDetail')}
        onEdit={(equipment) => {
          closeModal('equipmentDetail');
          handleEditEquipment(equipment);
        }}
        onAddMaintenance={handleCreateMaintenance}
        onAddCalibration={handleCreateCalibration}
      />

      <CalibrationModal
        show={modals.calibration.show}
        calibration={modals.calibration.data}
        equipment={modals.calibration.equipment}
        onClose={() => {
          loadCalibrations();
          closeModal('calibration');
        }}
        onSubmit={handleSubmitCalibration}
      />

      <MaintenanceModal
        show={modals.maintenance.show}
        maintenance={modals.maintenance.data}
        equipment={modals.maintenance.equipment}
        onClose={() => closeModal('maintenance')}
        onSubmit={handleSubmitMaintenance}
      />

      <FaultModal
        show={modals.fault.show}
        fault={modals.fault.data}
        plant={modals.fault.plant}
        selectedEquipmentId={modals.fault.equipment?.id || null}
        equipments={equipments.equipments.filter(e => e.plantId === modals.fault.plant?.id)}
        onClose={() => closeModal('fault')}
        onSubmit={handleSubmitFault}
      />

      <FaultUpdateModal
        show={modals.faultUpdate.show}
        fault={modals.faultUpdate.data}
        onClose={() => closeModal('faultUpdate')}
        onSubmit={handleSubmitFault}
      />

      {/* Modal de Política de Privacidad (Ley 21.719) */}
      <PrivacyConsentModal
        show={showPrivacyModal}
        onAccept={(consent) => {
          setShowPrivacyModal(false);
          auth.updateUser({ privacy_accepted_at: new Date().toISOString(), ...consent });
        }}
      />

      {/* Modal Mis Datos (ARCO) */}
      <MyDataModal
        show={modals.myData.show}
        user={auth.user}
        onClose={() => closeModal('myData')}
        onAccountDeleted={() => {
          closeModal('myData');
          auth.logout();
        }}
      />

      {/* PWA Install Banner */}
      {showInstallBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-800 border-t-2 border-cyan-500 px-4 py-3 flex items-center justify-between gap-3 shadow-lg">
          <p className="text-white text-sm">Instala Sistema PRT en tu dispositivo</p>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={async () => {
                if (installPrompt) {
                  await installPrompt.prompt();
                  setShowInstallBanner(false);
                  setInstallPrompt(null);
                }
              }}
              className="px-4 py-1.5 bg-cyan-500 text-slate-900 rounded font-semibold text-sm hover:bg-cyan-400"
            >
              Instalar
            </button>
            <button
              onClick={() => setShowInstallBanner(false)}
              className="p-1.5 text-slate-400 hover:text-white text-lg leading-none"
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Envuelve App con Sentry.ErrorBoundary para capturar crashes de React
// Si REACT_APP_SENTRY_DSN no está seteado, ErrorBoundary sigue funcionando
// pero sin enviar a Sentry (muestra fallback y no crashea en silencio)
export default Sentry.withErrorBoundary(App, {
  fallback: ({ error, resetError }) => (
    <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'sans-serif' }}>
      <h2>Algo salió mal</h2>
      <p style={{ color: '#666', marginBottom: '1rem' }}>{error?.message}</p>
      <button onClick={resetError} style={{ padding: '0.5rem 1.5rem', cursor: 'pointer' }}>
        Reintentar
      </button>
    </div>
  ),
  showDialog: false,
});
